import * as Sentry from '@sentry/react';
import invariant from 'tiny-invariant';
import {
  DoneInvokeEvent,
  EventObject,
  MachineConfig,
  assign,
  createMachine as machineGenerator,
} from 'xstate';

import { config as envConfig } from 'src/config';
import { DesignConfigCategories } from 'src/modules/designStudio/constants/designConfig';
import {
  ColorItemType,
  Colors,
  WindowColors,
} from 'src/modules/designStudio/constants/hardCodedData/colorData';
import { fetchData } from 'src/modules/designStudio/hooks/useFetchModelData';
import type {
  Geometry,
  Metadata,
} from 'src/modules/designStudio/hooks/useFetchModelData/types';
import { SplitClient } from 'src/modules/designStudio/machines/building';
import { Mode, Partner } from 'src/modules/designStudio/providers';
import { mapPartnerIdToEnum } from 'src/modules/designStudio/providers/ModeProvider';
import { DesignStudio } from 'src/shared/constants/FeatureFlag';
import { client as apolloClient } from 'src/shared/graphql';
import { Job, JobThreeDFiles, Maybe } from 'src/shared/graphql/generated';
import { GenericProductSettings, queries } from 'src/shared/graphql/requests';
import { requestWithRetry } from 'src/shared/helpers/request';

import {
  JamesHardieMenuItems,
  MenuItemType,
  MenuItems,
} from '../../constants/hardCodedData/textureData';
import { sharedOptions } from '../../machines/sharedOptions';
import { ProductEntry, Texture } from '../../types';

const PRODUCTS_ENDPOINT = envConfig.PRODUCTS_URI;

const GUEST_MACHETE_FILE_LOCATION = 'guest/machete_3_21_2022.json';
const GUEST_MODEL_FILE_LOCATION = 'guest/model_3_21_2022.json';

const GUEST_MACHETE_FILE_URL = `${
  process.env.PUBLIC_PATH || '/'
}${GUEST_MACHETE_FILE_LOCATION}`;
const GUEST_MODEL_FILE_URL = `${
  process.env.PUBLIC_PATH || '/'
}${GUEST_MODEL_FILE_LOCATION}`;

type Thumbnail = string | undefined;

type FilteredColorsData = {
  [key in DesignConfigCategories]?: ColorItemType[];
};

type FilteredTexturesData = {
  [key in DesignConfigCategories]?: {
    [key: string]: {
      [key: string]: boolean;
    };
  };
};

type FilterTracker = {
  [key in DesignConfigCategories]?: boolean;
};

export type Textures = ProductEntry[] | null;

interface Context {
  menuItems: MenuItemType[];
  textures: Textures;
  jobId?: number;
  filteredColors: FilteredColorsData;
  filteredTextures: FilteredTexturesData;
  productSettingsOrgId?: number;
  filterTracker: FilterTracker;
  geometry: Geometry;
  metadata: Metadata;
  mode: Mode;
  partner: Partner;
  splitClient: SplitClient;
  shouldShowPartnerProducts: boolean;
}

interface Schema {
  states: {
    unknown: {};
    fetchProductSettingOrgId: {};
    setMenuItems: {};
    fetchModelData: {};
    setup: {};
    fetchRunningExperiments: {};
    idle: {};
    fetchProductSettings: {};
    end: {};
  };
}

type FilterMenuItemsEvent = {
  type: 'FILTER_MENU_ITEMS_EVENT';
  data: GenericProductSettings;
};

type UpdateExperimentsEvent = {
  type: 'UPDATE_EXPERIMENTS_EVENT';
  data: {
    shouldShowPartnerProducts: boolean;
  };
};

type FetchProductCatalogEvent = {
  type: 'FETCH_PRODUCT_CATALOG_EVENT';
  data: { textures: Texture };
};

type Event = FetchProductCatalogEvent | EventObject | UpdateExperimentsEvent;

const setMenuItems = ({
  jhMenuItems,
  menuItems,
  mode,
  partner,
  partnerId,
  shouldShowPartnerProducts,
  textures,
}: {
  jhMenuItems: MenuItemType[];
  menuItems: MenuItemType[];
  mode: Mode;
  partner: Partner;
  partnerId?: number;
  shouldShowPartnerProducts: boolean;
  textures: Textures;
}) => {
  let partnerDeterminedFromJob: Partner | undefined;

  if (partnerId) {
    partnerDeterminedFromJob = mapPartnerIdToEnum(partnerId);
  }

  const items =
    (mode === Mode.GUEST && partner === Partner.JAMES_HARDIE) ||
    (shouldShowPartnerProducts &&
      partnerDeterminedFromJob === Partner.JAMES_HARDIE)
      ? jhMenuItems
      : menuItems;

  // add thumbnail urls from products endpoint
  const hydratedMenuItems = items.map(item => {
    const thumbnail: Thumbnail = item.hasThumbnail
      ? textures?.find(texture => texture.id === item.textureId)?.texture
          ?.thumbnail.url
      : undefined;

    return {
      ...item,
      thumbnail,
    };
  });

  return {
    menuItems: hydratedMenuItems,
    // partner from context was determined by query parameter
    partner: partner || partnerDeterminedFromJob,
  };
};

const filterMenuItems = (
  menuItems: MenuItemType[],
  filterTracker: FilterTracker,
  settings?: GenericProductSettings,
) => {
  const updatedFilterTracker = filterTracker;
  if (settings) {
    const filteredMenuItems = menuItems.filter(menuItem => {
      if (settings[menuItem.category]?.disabled_types) {
        updatedFilterTracker[
          menuItem.category as DesignConfigCategories
        ] = true;
      }
      return !settings[menuItem.category]?.disabled_types?.includes(
        menuItem.type,
      );
    });

    return {
      menuItems: filteredMenuItems,
      filterTracker: updatedFilterTracker,
    };
  }
  return {};
};

const filterTextures = (
  menuItems: MenuItemType[],
  filterTracker: FilterTracker,
  settings?: GenericProductSettings,
) => {
  const filteredTextures: FilteredTexturesData = {};
  const updatedFilterTracker = filterTracker;

  const isMenuItemFilteredById = (
    menuItem: MenuItemType,
    disabledTextureIds: { [key: string]: boolean | undefined },
  ) =>
    !!disabledTextureIds[
      `${menuItem.type.toUpperCase()}::${menuItem.textureId}`
    ];

  const isMenuItemFilteredByColorGroup = (
    menuItem: MenuItemType,
    disabledTextureColorGroups: { [key: string]: boolean | undefined },
  ) =>
    !!disabledTextureColorGroups[
      `${menuItem.type.toUpperCase()}::${
        menuItem.colorGroup?.toUpperCase() ?? ''
      }`
    ];

  if (settings) {
    Object.keys(settings).forEach(category => {
      const filteredCategoryTextures =
        filteredTextures[category as DesignConfigCategories] ?? {};
      const categorySettings = settings[category] ?? {};

      const disabledTextureIds: { [key: string]: boolean } = {};
      (categorySettings.disabled_textures ?? []).forEach(disabledTexture => {
        disabledTextureIds[disabledTexture.toUpperCase()] = true;
      });

      const disabledTextureColorGroups: { [key: string]: boolean } = {};
      (categorySettings.disabled_texture_color_groups ?? []).forEach(
        disabledColorGroup => {
          disabledTextureColorGroups[disabledColorGroup.toUpperCase()] = true;
        },
      );

      menuItems.forEach(menuItem => {
        const isDisabled =
          isMenuItemFilteredById(menuItem, disabledTextureIds) ||
          isMenuItemFilteredByColorGroup(menuItem, disabledTextureColorGroups);

        if (isDisabled) {
          const filteredTypeTextures =
            filteredCategoryTextures[menuItem.type] || {};
          filteredTypeTextures[menuItem.textureId] = true;
          filteredCategoryTextures[menuItem.type] = filteredTypeTextures;
        }
      });

      if (Object.keys(filteredCategoryTextures).length > 0) {
        filteredTextures[
          category as DesignConfigCategories
        ] = filteredCategoryTextures;
      }
    });
  }

  return { filteredTextures, filterTracker: updatedFilterTracker };
};

const filterColors = (
  filterTracker: FilterTracker,
  settings?: GenericProductSettings,
) => {
  const filteredColors: FilteredColorsData = {};
  const updatedFilterTracker = filterTracker;

  if (settings) {
    Object.keys(settings).forEach(category => {
      const colorDataSet: ColorItemType[] =
        category === DesignConfigCategories.WINDOW ? WindowColors : Colors;

      const filtered = colorDataSet.filter(color => {
        if (settings[category]?.disabled_colors) {
          updatedFilterTracker[category as DesignConfigCategories] = true;
        }
        return !settings[category]?.disabled_colors?.includes(color.color);
      });

      filteredColors[category as DesignConfigCategories] = filtered;
    });
  }

  return { filteredColors, filterTracker: updatedFilterTracker };
};

const createMachineConfig = (
  mode: Mode = Mode.DEFAULT,
  partner: Partner = Partner.DEFAULT,
  splitClient: SplitClient,
  jobId?: number,
): MachineConfig<Context, Schema, Event> => ({
  id: 'product-catalog',
  context: {
    menuItems: [],
    textures: null,
    jobId,
    filteredColors: {},
    filteredTextures: {},
    filterTracker: {},
    geometry: null,
    metadata: null,
    mode,
    partner,
    splitClient,
    shouldShowPartnerProducts: false,
  },
  initial: 'unknown',
  states: {
    unknown: {
      always: [
        { target: 'setup', cond: 'modeIsShare' },
        { target: 'fetchModelData', cond: 'modeIsGuest' },
        { target: 'fetchProductSettingOrgId' },
      ],
    },
    fetchProductSettingOrgId: {
      invoke: {
        src: 'fetchProductSettingOrgId',
        onDone: {
          target: 'fetchModelData',
          actions: ['updateProductSettingsOrgId'],
        },
        onError: {
          target: 'fetchModelData',
          actions: ['logError'],
        },
      },
    },
    fetchModelData: {
      invoke: {
        src: 'fetchModelData',
        onDone: {
          target: 'setup',
          actions: ['updateModelData'],
        },
        onError: {
          target: 'setup',
          actions: ['logError'],
        },
      },
    },
    setup: {
      invoke: {
        src: 'fetchProductCatalog',
        onDone: [
          {
            target: 'fetchRunningExperiments',
            actions: 'assignTextures',
          },
        ],
        onError: {
          target: 'end',
          actions: 'logError',
        },
      },
    },
    fetchRunningExperiments: {
      invoke: {
        src: 'fetchRunningExperiments',
        onDone: {
          target: 'setMenuItems',
          actions: 'setExperiments',
        },
        onError: {
          target: 'setMenuItems',
          actions: 'logError',
        },
      },
    },
    setMenuItems: {
      invoke: {
        src: 'fetchPartnerId',
        onDone: [
          {
            target: 'fetchProductSettings',
            actions: ['setMenuItems'],
            cond: ctx => !!ctx.productSettingsOrgId,
          },
          {
            target: 'idle',
            actions: ['setMenuItems'],
          },
        ],
        onError: {
          target: 'idle',
          actions: ['setMenuItems', 'logError'],
        },
      },
    },
    idle: {
      preserveActionOrder: true,
      on: {
        UPDATE_EXPERIMENTS_EVENT: [
          {
            actions: ['setExperiments', 'setMenuItems'],
          },
        ],
      },
    },
    fetchProductSettings: {
      invoke: {
        src: 'fetchProductSettings',
        onDone: {
          actions: ['filterMenuItems', 'filterColors', 'filterTextures'],
        },
        onError: { target: 'end', actions: 'logError' },
      },
      preserveActionOrder: true,
      on: {
        UPDATE_EXPERIMENTS_EVENT: [
          {
            actions: ['setExperiments', 'setMenuItems'],
            target: 'fetchProductSettings',
          },
        ],
      },
    },
    end: {
      type: 'final',
    },
  },
});

const options = {
  services: {
    fetchRunningExperiments: async (ctx: Context) => {
      const splitClient = await ctx.splitClient;
      const shouldShowPartnerProducts =
        splitClient?.getTreatment(DesignStudio.DS_JH_PRODUCTS) === 'on';

      return { shouldShowPartnerProducts };
    },
    fetchPartnerId: async (ctx: Context, evt: Event) => {
      const {
        data: { textures },
      } = evt as FetchProductCatalogEvent;

      const partnerId = ctx.jobId
        ? await sharedOptions.fetchPartnerIdForJob(ctx.jobId)
        : undefined;

      return {
        textures,
        partnerId,
      };
    },
    fetchProductSettingOrgId: async (ctx: Context) => {
      invariant(ctx.jobId, 'Missing jobId');
      const jobResponse = await queries.getJob(apolloClient, { id: ctx.jobId });
      return jobResponse.data.jobs.results[0];
    },
    fetchModelData: async (ctx: Context) => {
      let threeDFiles: JobThreeDFiles;
      if (ctx.mode === Mode.GUEST) {
        threeDFiles = {
          geometryUrl: GUEST_MODEL_FILE_URL,
          metadataUrl: GUEST_MACHETE_FILE_URL,
        };
      } else {
        invariant(ctx.jobId, 'Missing jobId');
        const job = await queries.getJob(apolloClient, { id: ctx.jobId });
        threeDFiles = job.data.jobs.results[0]?.threeDFiles || {};
      }
      return fetchData(threeDFiles);
    },
    fetchProductSettings: async (ctx: Context) => {
      if (ctx.productSettingsOrgId) {
        const response = await queries.getGenericProductSettings(apolloClient, {
          orgId: ctx.productSettingsOrgId.toString(),
        });

        if (!response) return null;

        const settings: GenericProductSettings = { ...response };

        Object.keys(response).forEach(key => {
          if (key && response[key] && response[key].disabled_types) {
            const mutatedTypes = response[key]?.disabled_types?.map(type =>
              type.toLowerCase(),
            );
            settings[key] = {
              ...response[key],
              disabled_types: mutatedTypes,
            };
          }
        });

        return settings;
      }
      return null;
    },
    fetchProductCatalog: async () => {
      const textures = (await requestWithRetry(PRODUCTS_ENDPOINT)
        .then(res => res.json())
        .catch(error => {
          Sentry.captureException(error);

          return [];
        })) as Textures;

      return {
        textures,
      };
    },
  },
  actions: {
    setExperiments: assign((_: Context, _event) => {
      const event = _event as DoneInvokeEvent<{
        shouldShowPartnerProducts: boolean;
      }>;

      return event.data;
    }),
    setMenuItems: assign((ctx: Context, _event) => {
      const event = _event as DoneInvokeEvent<{
        partnerId?: number;
      }>;

      const { partnerId } = event.data;

      return setMenuItems({
        jhMenuItems: JamesHardieMenuItems,
        menuItems: MenuItems,
        mode: ctx.mode,
        partner: ctx.partner,
        partnerId,
        shouldShowPartnerProducts: ctx.shouldShowPartnerProducts,
        textures: ctx.textures,
      });
    }),
    updateModelData: assign((_: Context, _event: any) => {
      const event = _event as { data: { geometry: object; metadata: object } };
      return event.data;
    }),
    filterMenuItems: assign((ctx: Context, _event: any) => {
      const event = _event as FilterMenuItemsEvent;
      const settings = event.data;

      return filterMenuItems(ctx.menuItems, ctx.filterTracker, settings);
    }),
    filterColors: assign((ctx: Context, _event: any) => {
      const event = _event as FilterMenuItemsEvent;
      const settings = event.data;

      return filterColors(ctx.filterTracker, settings);
    }),
    filterTextures: assign((ctx: Context, _event: any) => {
      const event = _event as FilterMenuItemsEvent;
      const settings = event.data;

      return filterTextures(ctx.menuItems, ctx.filterTracker, settings);
    }),
    updateProductSettingsOrgId: assign((_: Context, _event: any) => {
      const { data } = _event as DoneInvokeEvent<Maybe<Job>>;

      if (!data) {
        return {};
      }

      const { orgId, captureRequest } = data;

      let productSettingsOrgId = orgId ?? undefined;

      if (captureRequest && captureRequest.requestingUser) {
        const requestingOrg = captureRequest.requestingUser.orgs.results[0];
        if (requestingOrg) {
          productSettingsOrgId = requestingOrg.id;
        }
      }

      return { productSettingsOrgId };
    }),
    assignTextures: assign((_: Context, _event: any) => {
      const event = _event as DoneInvokeEvent<{
        textures: Textures;
      }>;

      const { textures } = event.data;
      return { textures };
    }),
    logError: sharedOptions.logError,
  },
  guards: {
    modeIsShare: (ctx: Context) => ctx.mode === Mode.SHARE,
    modeIsDefault: (ctx: Context) => ctx.mode === Mode.DEFAULT,
    modeIsGuest: (ctx: Context) => ctx.mode === Mode.GUEST,
  },
};

const createMachine = (
  mode: Mode,
  partner: Partner,
  splitClient: SplitClient,
  jobId?: number,
) =>
  machineGenerator<Context, Event>(
    createMachineConfig(mode, partner, splitClient, jobId),
    options,
  );

export {
  Context,
  createMachine,
  createMachineConfig,
  Event,
  filterColors,
  FilteredColorsData,
  FilteredTexturesData,
  filterMenuItems,
  filterTextures,
  FilterTracker,
  options,
  setMenuItems,
  UpdateExperimentsEvent,
};
